import { useState, useCallback, useContext, useEffect } from "react";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import QboAttachmentsService from "../../../services/QboAttachmentsService";
import { IQboAttachment } from "../../../models/DTO/IQboAttachmentDTO";
import { useAppContextNonNull } from "@contexts/AppContext";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import INotification from "@models/INotification";

interface UseQboAttachmentsOptions {
    qboEntityId: string;
    accountingId?: number;
    onUploadComplete?: (attachment: IQboAttachment) => void;
    onDeleteComplete?: (attachmentGuid: string) => void;
    onError?: (error: string) => void;
    autoLoad?: boolean;
}

interface UseQboAttachmentsReturn {
    attachments: IQboAttachment[];
    isLoading: boolean;
    isUploading: boolean;
    uploadProgress: number;
    error: string | null;
    includeOnSend: boolean;
    setIncludeOnSend: (value: boolean) => void;
    loadAttachments: () => Promise<void>;
    uploadFile: (file: File) => Promise<void>;
    deleteAttachment: (attachmentGuid: string) => Promise<void>;
    updateAttachment: (attachmentGuid: string, includeOnSend: boolean) => Promise<void>;
    downloadAttachment: (attachment: IQboAttachment) => Promise<void>;
    clearError: () => void;
}

const ALLOWED_MIME_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/svg+xml",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export const useQboAttachments = (options: UseQboAttachmentsOptions): UseQboAttachmentsReturn => {
    const { qboEntityId, accountingId, onUploadComplete, onDeleteComplete, onError, autoLoad = true } = options;

    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const [attachments, setAttachments] = useState<IQboAttachment[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [includeOnSend, setIncludeOnSend] = useState(false);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const handleError = useCallback(
        (errorMessage: string) => {
            setError(errorMessage);
            if (onError) {
                onError(errorMessage);
            }
        },
        [onError]
    );

    const loadAttachments = useCallback(async () => {
        if (!qboEntityId) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await QboAttachmentsService.getAttachments(qboEntityId, userModel, supplierModel);
            setAttachments(response.attachments || []);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to load attachments";
            handleError(message);
        } finally {
            setIsLoading(false);
        }
    }, [qboEntityId, userModel, supplierModel, handleError]);

    const validateFile = useCallback((file: File): string | null => {
        if (file.size > MAX_FILE_SIZE) {
            return `File exceeds maximum size of 100MB`;
        }

        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
            return `Invalid file type. Allowed: PDF, images, Word, Excel`;
        }

        return null;
    }, []);

    const uploadFile = useCallback(
        async (file: File) => {
            const validationError = validateFile(file);
            if (validationError) {
                handleError(validationError);
                return;
            }

            setIsUploading(true);
            setUploadProgress(0);
            setError(null);

            try {
                const response = await QboAttachmentsService.uploadAttachment(qboEntityId, file, userModel, supplierModel, {
                    accountingId,
                    includeOnSend,
                    onProgress: (percent) => setUploadProgress(percent),
                });

                if (response.success && response.attachment) {
                    setAttachments((prev) => [...prev, response.attachment!]);
                    if (onUploadComplete) {
                        onUploadComplete(response.attachment);
                    }
                } else {
                    throw new Error(response.errorMessage || "Upload failed");
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to upload file";
                handleError(message);
            } finally {
                setIsUploading(false);
                setUploadProgress(0);
            }
        },
        [qboEntityId, userModel, supplierModel, accountingId, includeOnSend, validateFile, handleError, onUploadComplete]
    );

    const deleteAttachment = useCallback(
        async (attachmentGuid: string) => {
            setError(null);

            try {
                const response = await QboAttachmentsService.deleteAttachment(qboEntityId, attachmentGuid, userModel, supplierModel);

                if (response.success) {
                    const notification: INotification = {
                        message: "successfully_deleted_attachment",
                        level: NotificationLevelEnum.success,
                        position: NotificationPositionEnum.bc,
                    };

                    uiContext.notifyFn(null, notification);

                    setAttachments((prev) => prev.filter((a) => a.guid !== attachmentGuid));
                    if (onDeleteComplete) {
                        onDeleteComplete(attachmentGuid);
                    }
                } else {
                    throw new Error(response.errorMessage || "Delete failed");
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to delete attachment";
                handleError(message);
            }
        },
        [qboEntityId, userModel, supplierModel, handleError, onDeleteComplete]
    );

    const updateAttachment = useCallback(
        async (attachmentGuid: string, newIncludeOnSend: boolean) => {
            setError(null);

            try {
                const response = await QboAttachmentsService.updateAttachment(
                    qboEntityId,
                    attachmentGuid,
                    { includeOnSend: newIncludeOnSend },
                    userModel,
                    supplierModel
                );

                if (response.success && response.attachment) {
                    const notification: INotification = {
                        message: "successfully_updated_attachment",
                        level: NotificationLevelEnum.success,
                        position: NotificationPositionEnum.bc,
                    };

                    uiContext.notifyFn(null, notification);

                    setAttachments((prev) => prev.map((a) => (a.guid === attachmentGuid ? response.attachment! : a)));
                } else {
                    throw new Error(response.errorMessage || "Update failed");
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to update attachment";
                handleError(message);
            }
        },
        [qboEntityId, userModel, supplierModel, handleError]
    );

    const downloadAttachment = useCallback(
        async (attachment: IQboAttachment) => {
            try {
                const response = await QboAttachmentsService.getDownloadUrl(qboEntityId, attachment.guid, userModel, supplierModel);
                if (response.downloadUrl) {
                    window.open(response.downloadUrl, "_blank");
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to get download URL";
                handleError(message);
            }
        },
        [qboEntityId, userModel, supplierModel, handleError]
    );

    useEffect(() => {
        if (autoLoad && qboEntityId) {
            loadAttachments();
        }
    }, [autoLoad, qboEntityId, loadAttachments]);

    return {
        attachments,
        isLoading,
        isUploading,
        uploadProgress,
        error,
        includeOnSend,
        setIncludeOnSend,
        loadAttachments,
        uploadFile,
        deleteAttachment,
        updateAttachment,
        downloadAttachment,
        clearError,
    };
};

export default useQboAttachments;
